import { LitElement, html, css, nothing } from "lit";
import { customElement } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import { closeModal } from "../ops/ui.js";
import { UNIT_LABEL, type DxfUnit } from "../state/types.js";
import { addLayer } from "../ops/layers.js";
import { moveSelectedToLayer } from "../ops/entities.js";
import { applyUnitSettings } from "../ops/units.js";
import { exportAndDownload } from "../ops/io.js";
import { toast } from "../ops/ui.js";

// Discriminated modal driven by the store's `modal.kind`. Renders the right
// form + button behavior. Renderer stays flat — one component, one file.
@customElement("dxf-modal")
export class DxfModal extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 2000;
      display: none;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.6);
      padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px)
        env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
      box-sizing: border-box;
    }
    :host(.open) { display: flex; }

    .modal {
      background: var(--bg-tertiary);
      border-radius: 8px;
      padding: 24px;
      min-width: 380px;
      max-width: 90vw;
      max-height: calc(100vh - 32px);
      overflow-y: auto;
      box-shadow: 0 20px 60px var(--shadow);
      color: var(--text-primary);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      box-sizing: border-box;
    }

    h3 { margin: 0 0 16px; font-weight: 500; }

    label {
      display: block;
      color: var(--text-secondary);
      font-size: 12px;
      margin: 8px 0 4px;
    }

    input, select {
      width: 100%;
      padding: 8px 12px;
      background: var(--bg-secondary);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      color: var(--text-primary);
      font-size: 14px;
      box-sizing: border-box;
    }

    input:focus, select:focus { outline: none; border-color: var(--accent); }

    .status {
      padding: 8px 12px;
      background: var(--bg-secondary);
      border-radius: 4px;
      margin: 8px 0 12px;
      font-size: 13px;
      border-left: 3px solid var(--accent);
    }

    .status.warn { border-left-color: #f0a030; }

    .actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-top: 16px;
    }

    button {
      padding: 6px 20px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 13px;
    }

    button.cancel { background: var(--bg-secondary); color: var(--text-primary); }
    button.confirm { background: var(--accent); color: #fff; }
    button.confirm:hover { background: var(--accent-hover); }
    button.confirm.danger { background: var(--danger, #c14545); }
    button.confirm.danger:hover { filter: brightness(1.1); }

    /* Mobile: modal slides up from the bottom of the viewport and fills
       the width, matching iOS / Android sheet ergonomics. min-width
       drops so a 320px viewport doesn't force horizontal scroll; the
       action row becomes full-width primary + full-width cancel stacked
       so touch users don't have to aim at 60px buttons. */
    @media (max-width: 720px) {
      :host {
        align-items: flex-end;
      }
      .modal {
        width: 100%;
        min-width: 0;
        max-width: 100%;
        padding: 20px 20px calc(24px + var(--safe-bottom, 0px));
        border-radius: 16px 16px 0 0;
        animation: modal-up 240ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .actions {
        flex-direction: column-reverse;
        gap: 8px;
      }
      .actions button {
        width: 100%;
        padding: 12px 20px;
        font-size: 15px;
        min-height: 48px;
      }
      input, select {
        font-size: 16px; /* Prevents iOS zoom on focus. */
        padding: 12px 12px;
        min-height: 44px;
      }
      h3 {
        font-size: 18px;
      }
    }
    @keyframes modal-up {
      from { transform: translateY(24%); opacity: 0.6; }
      to   { transform: translateY(0); opacity: 1; }
    }
  `;

  private wasOpen = false;

  override render() {
    const m = store.modal();
    if (!m.open || !m.kind) {
      this.classList.remove("open");
      this.wasOpen = false;
      return nothing;
    }
    this.classList.add("open");

    switch (m.kind) {
      case "add-layer":
        return this.#renderNamePrompt("New Layer", "Layer name...", "", (v) => {
          if (v) addLayer(v);
        });
      case "rename-layer":
        // Rename now happens inline in the layer panel — the "rename-layer"
        // ModalKind stays in the union for backwards compatibility with any
        // consumer that still opens it, but nothing should render a modal
        // for it. Fall through to `nothing`.
        return nothing;
      case "move-to-layer":
        return this.#renderMoveToLayer(m.data.onConfirm as (name: string) => void);
      case "move-to-new-layer":
        return this.#renderMoveToNewLayer();
      case "unit-settings":
        return this.#renderUnitSettings();
      case "export":
        return this.#renderExport();
      case "confirm":
        return this.#renderConfirm();
      default:
        return nothing;
    }
  }

  override updated(): void {
    // Focus the first input whenever the modal transitions from closed → open.
    // Doing this in updated() (rather than the render template's autofocus
    // attribute) is more reliable inside a shadow root. The confirmation
    // variant has no input; we focus the modal container itself so Enter /
    // Esc route through its keydown handler.
    const m = store.modal();
    if (m.open && !this.wasOpen) {
      this.wasOpen = true;
      queueMicrotask(() => {
        const input = this.renderRoot.querySelector<HTMLInputElement>(
          "input#name-input, input#modal-input, input#export-name"
        );
        if (input) {
          input.focus();
          input.select();
          return;
        }
        const focusable = this.renderRoot.querySelector<HTMLElement>(
          ".modal[tabindex]"
        );
        focusable?.focus();
      });
    } else if (!m.open) {
      this.wasOpen = false;
    }
  }

  #renderNamePrompt(
    title: string,
    placeholder: string,
    initial: string,
    onConfirm: (value: string) => void
  ) {
    const submit = () => {
      const input = this.renderRoot.querySelector<HTMLInputElement>("#name-input");
      if (!input) return;
      onConfirm(input.value.trim());
      closeModal();
    };
    return html`
      <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
        <h3>${title}</h3>
        <input
          id="name-input"
          type="text"
          placeholder=${placeholder}
          .value=${initial}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") closeModal();
          }}
        />
        <div class="actions">
          <button class="cancel" @click=${closeModal}>Cancel</button>
          <button class="confirm" @click=${submit}>OK</button>
        </div>
      </div>
    `;
  }

  // Prompt for a layer name and move the current selection into it. If the
  // typed name matches an existing layer, we skip creation and just move.
  // Empty input cancels silently.
  #renderMoveToNewLayer() {
    return this.#renderNamePrompt(
      "Move to New Layer",
      "Layer name...",
      "",
      (name) => {
        if (!name) return;
        const existing = store.layers()[name];
        if (existing) {
          const moved = moveSelectedToLayer(name);
          if (moved > 0) toast(`Moved ${moved} to existing "${name}"`);
          return;
        }
        const created = addLayer(name);
        if (created) {
          const moved = moveSelectedToLayer(created);
          if (moved > 0) toast(`Moved ${moved} to new "${created}"`);
        }
      }
    );
  }

  #renderMoveToLayer(onConfirm: (name: string) => void) {
    const order = store.layerOrder();
    const anchor = store.selectedLayer();
    const submit = () => {
      const sel = this.renderRoot.querySelector<HTMLSelectElement>("#layer-target");
      if (!sel) return;
      const value = sel.value;
      if (value === "__new__") {
        const name = prompt("New layer name:");
        if (name && name.trim()) {
          const created = addLayer(name.trim());
          if (created) onConfirm(created);
        }
      } else {
        onConfirm(value);
      }
      closeModal();
    };
    return html`
      <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
        <h3>Move Entities</h3>
        <label>Target layer</label>
        <select id="layer-target">
          ${order.map(
            (n) =>
              html`<option value=${n} ?selected=${n === anchor}>${n}</option>`
          )}
          <option value="__new__">+ Create new layer...</option>
        </select>
        <div class="actions">
          <button class="cancel" @click=${closeModal}>Cancel</button>
          <button class="confirm" @click=${submit}>OK</button>
        </div>
      </div>
    `;
  }

  #renderUnitSettings() {
    const submit = () => {
      const dxfSel = this.renderRoot.querySelector<HTMLSelectElement>("#dxf-unit");
      const viewerSel = this.renderRoot.querySelector<HTMLSelectElement>("#viewer-unit");
      const gridSel = this.renderRoot.querySelector<HTMLSelectElement>("#grid-unit");
      const spacing = this.renderRoot.querySelector<HTMLInputElement>("#grid-spacing");
      if (!dxfSel || !viewerSel || !gridSel || !spacing) return;
      applyUnitSettings({
        dxfUnit: dxfSel.value as DxfUnit,
        viewerUnit: viewerSel.value as DxfUnit,
        gridUnit: gridSel.value as DxfUnit,
        gridSpacing: Number.parseFloat(spacing.value) || 1,
      });
      closeModal();
    };
    const units = UNIT_ORDER.map((v) => [v, UNIT_LABEL[v]] as const);
    return html`
      <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
        <h3>Units &amp; Grid Settings</h3>
        <p style="font-size:12px; color:var(--text-secondary); margin:0 0 12px;">
          Editor-specific. Loading a DXF won't change these; the top-bar
          dimensions and export dropdown both derive from the two unit
          settings below.
        </p>

        <label>DXF Drawing Units</label>
        <select id="dxf-unit">
          ${units.map(
            ([v, l]) =>
              html`<option value=${v} ?selected=${v === store.dxfUnit()}>${l}</option>`
          )}
        </select>

        <label>Editor/Viewer Units</label>
        <select id="viewer-unit">
          ${units.map(
            ([v, l]) =>
              html`<option value=${v} ?selected=${v === store.viewerUnit()}>${l}</option>`
          )}
        </select>

        <label>Grid Display Units</label>
        <select id="grid-unit">
          ${units.map(
            ([v, l]) =>
              html`<option value=${v} ?selected=${v === store.gridUnit()}>${l}</option>`
          )}
        </select>

        <label>Grid Spacing</label>
        <input id="grid-spacing" type="number" step="0.1" min="0.1" .value=${String(store.gridSpacing())} />

        <div class="actions">
          <button class="cancel" @click=${closeModal}>Cancel</button>
          <button class="confirm" @click=${submit}>Apply</button>
        </div>
      </div>
    `;
  }

  // Generic yes/cancel confirmation. Caller passes shape via `m.data`:
  //   { title, message, confirmLabel?, cancelLabel?, danger?, onConfirm }
  // `onConfirm` runs the underlying action; the modal handles Cancel/close
  // + Enter/Esc keys uniformly across every confirmation dialog.
  #renderConfirm() {
    const m = store.modal();
    const title = (m.data.title as string) ?? "Are you sure?";
    const message = (m.data.message as string) ?? "";
    const confirmLabel = (m.data.confirmLabel as string) ?? "Yes";
    const cancelLabel = (m.data.cancelLabel as string) ?? "Cancel";
    const danger = m.data.danger === true;
    const onConfirm = m.data.onConfirm as (() => void) | undefined;

    const submit = () => {
      closeModal();
      if (onConfirm) onConfirm();
    };
    return html`
      <div
        class="modal"
        tabindex="0"
        @click=${(e: Event) => e.stopPropagation()}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") closeModal();
        }}
      >
        <h3>${title}</h3>
        ${message
          ? html`<p style="margin: 0 0 16px; font-size: 13px; line-height: 1.5;">
              ${message}
            </p>`
          : nothing}
        <div class="actions">
          <button class="cancel" @click=${closeModal}>${cancelLabel}</button>
          <button
            class=${danger ? "confirm danger" : "confirm"}
            @click=${submit}
          >
            ${confirmLabel}
          </button>
        </div>
      </div>
    `;
  }

  #renderExport() {
    const dxfUnit = store.dxfUnit();
    const viewerUnit = store.viewerUnit();
    // Deduplicate when the two settings happen to be identical so the
    // dropdown doesn't show two options that produce the same file.
    const options: Array<{ value: DxfUnit; label: string }> = [
      { value: dxfUnit, label: `DXF Drawing Units (${dxfUnit})` },
    ];
    if (viewerUnit !== dxfUnit) {
      options.push({ value: viewerUnit, label: `Editor/Viewer Units (${viewerUnit})` });
    }

    const submit = () => {
      const nameInput = this.renderRoot.querySelector<HTMLInputElement>("#export-name");
      const unitSel = this.renderRoot.querySelector<HTMLSelectElement>("#export-unit");
      if (!nameInput || !unitSel) return;
      let filename = nameInput.value.trim();
      if (!filename) filename = defaultExportName(store.filename());
      if (!/\.[dD][xX][fF]$/.test(filename)) filename += ".dxf";
      exportAndDownload(filename, unitSel.value as DxfUnit);
      closeModal();
    };

    return html`
      <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
        <h3>Export DXF</h3>

        <label>Filename</label>
        <input
          id="export-name"
          type="text"
          .value=${defaultExportName(store.filename())}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") closeModal();
          }}
        />

        <label>Output units</label>
        <select id="export-unit">
          ${options.map(
            (o) =>
              html`<option value=${o.value} ?selected=${o.value === viewerUnit}>${o.label}</option>`
          )}
        </select>

        <div class="actions">
          <button class="cancel" @click=${closeModal}>Cancel</button>
          <button class="confirm" @click=${submit}>Export</button>
        </div>
      </div>
    `;
  }
}

// Order the units in the same familiar sequence across every modal.
const UNIT_ORDER: DxfUnit[] = ["mm", "cm", "m", "in", "ft"];

// Turn a loaded filename into a sensible default export name. Preserves
// the base but appends `_exported` so we don't silently overwrite the
// source; falls back to a generic name when nothing is loaded.
function defaultExportName(source: string | null): string {
  if (!source) return "exported.dxf";
  const base = source.replace(/\.[dD][xX][fF]$/, "");
  return `${base}_exported.dxf`;
}

declare global {
  interface HTMLElementTagNameMap {
    "dxf-modal": DxfModal;
  }
}
